import { Public, Meta } from '../lib/ui';
type Props = {
  className?: string;
};

const Content = () => (
  <>
    <h3>Who We Are and What This Policy Covers</h3>
    <p>
      Howdy! This Privacy Policy applies to information that we collect about
      you when you use our website Postdrop.io
    </p>
    <p>
      Throughout this Privacy Policy we’ll refer to our website, mobile
      applications, and other products and services collectively as “Services.”
      Below we explain how we collect, use, and share information about you,
      along with the choices that you have with respect to that information.
    </p>
    <h3>Information We Collect</h3>
    <p>
      We only collect information about you if we have a reason to do so — for
      example, to provide our Services, to communicate with you, or to make our
      Services better. We collect this information from three sources: if and
      when you provide information to us, automatically through operating our
      Services, and from outside sources. Let’s go over the information that we
      collect.
    </p>
    <h4>Information You Provide to Us</h4>
    <p>
      It’s probably no surprise that we collect information that you provide to
      us directly. Here are some examples:
    </p>
    <ul>
      <li>
        Basic account information: We ask for basic information from you in
        order to set up your account. For example, we require individuals who
        sign up for a Postdrop account to provide an email address and password,
        along with a name and company name — and that’s it. You may provide us
        with more information — like your role and other information you want to
        share — but we don’t require that information to create a Postdrop
        account.
      </li>
      <li>
        Payment and contact information: If you buy something from us we’ll
        collect information to process those payments and contact you. If you
        buy something from us — a subscription to a Postdrop plan — you’ll
        provide additional personal and payment information like your name,
        credit card information, and contact information. We also keep a record
        of the purchases you’ve made.
      </li>
      <li>
        Content information: You might provide us with information about you in
        draft emails.
      </li>
      <li>
        Communications with us (hi there!): You may also provide us with
        information when you respond to surveys, communicate with our support
        team, post a question in our public forums, or sign up for a newsletter.
        When you communicate with us via form, email, phone, or otherwise, we
        store a copy of our communications.
      </li>
    </ul>
    <h4>Information We Collect Automatically</h4>
    <p>We also collect some information automatically:</p>
    <ul>
      <li>
        Log information: Like most online service providers, we collect
        information that web browsers, mobile devices, and servers typically
        make available, including the browser type, IP address, unique device
        identifiers, language preference, referring site, the date and time of
        access, operating system, and mobile network information. We collect log
        information when you use our Services — for example, when you create or
        make changes to an email on Postdrop.
      </li>
      <li>
        Usage information: We collect information about your usage of our
        Services. For example, we collect information about the actions that
        users perform on a site — in other words, who did what and when. We also
        collect information about what happens when you use our Services (e.g.,
        page views, features enabled for your website) along with information
        about your device (e.g., screen size, name of cellular network, and
        mobile device manufacturer). We use this information to, for example,
        provide our Services to you, get insights on how people use our Services
        so we can make our Services better, and understand and make predictions
        about user retention.
      </li>
      <li>
        Location information: We may determine the approximate location of your
        device from your IP address. We collect and use this information to, for
        example, calculate how many people visit our Services from certain
        geographic regions. We may also collect information about your precise
        location via our mobile apps (like when you post a photograph with
        location information) if you allow us to do so through your mobile
        device operating system’s permissions.
      </li>
      <li>
        Information from cookies &amp; other technologies: A cookie is a string
        of information that a website stores on a visitor’s computer, and that
        the visitor’s browser provides to the website each time the visitor
        returns. Pixel tags (also called web beacons) are small blocks of code
        placed on websites and emails. Postdrop uses cookies and other
        technologies like pixel tags to help us identify and track visitors,
        usage, and access preferences for our Services, as well as track and
        understand email campaign effectiveness and to deliver targeted ads.
      </li>
    </ul>
    <h3>How and Why We Use Information</h3>
    <h4>Purposes for Using Information</h4>
    <p>We use information about you for the purposes listed below:</p>
    <ul>
      <li>
        To provide our Services. For example, to set up and maintain your
        account, host your website, backup and restore your website, provide
        customer service, process payments and orders, and verify user
        information.
      </li>
      <li>
        To ensure quality, maintain safety, and improve our Services. For
        example, by providing automatic upgrades and new versions of our
        Services. Or, for example, by monitoring and analyzing how users
        interact with our Services so we can create new features that we think
        our users will enjoy and that will help them create and manage websites
        more efficiently or make our Services easier to use.
      </li>
      <li>To place and manage ads in our advertising program.</li>
      <li>
        To market our Services and measure, gauge, and improve the effectiveness
        of our marketing. For example, by targeting our marketing messages to
        groups of our users (like those who have a particular plan with us or
        have been users for a certain length of time), advertising our Services,
        analyzing the results of our marketing campaigns (like how many people
        purchased a paid plan after receiving a marketing message), and
        understanding and forecasting user retention.
      </li>
      <li>
        To protect our Services, our users, and the public. For example, by
        detecting security incidents; detecting and protecting against
        malicious, deceptive, fraudulent, or illegal activity; fighting spam;
        complying with our legal obligations; and protecting the rights and
        property of Postdrop and others, which may result in us, for example,
        declining a transaction or terminating Services.
      </li>
      <li>
        To fix problems with our Services. For example, by monitoring,
        debugging, repairing, and preventing issues.
      </li>
      <li>
        To customize the user experience. For example, to personalize your
        experience by serving you relevant notifications and advertisements for
        our Services.
      </li>
      <li>
        To communicate with you. For example, by emailing you to ask for your
        feedback, share tips for getting the most out of our products, or keep
        you up to date on Postdrop; texting you to verify your payment; or
        calling you to share offers and promotions that we think will be of
        interest to you. If you don’t want to hear from us, you can opt out of
        marketing communications at any time. (If you opt out, we’ll still send
        you important updates relating to your account.)
      </li>
    </ul>
    <h4>Legal Bases for Collecting and Using Information</h4>
    <p>
      A note here for those in the European Union about our legal grounds for
      processing information about you under EU data protection laws, which is
      that our use of your information is based on the grounds that: (1) The use
      is necessary in order to fulfill our commitments to you under the
      applicable terms of service or other agreements with you or is necessary
      to administer your account — for example, in order to enable access to our
      website on your device or charge you for a paid plan; or (2) The use is
      necessary for compliance with a legal obligation; or (3) The use is
      necessary in order to protect your vital interests or those of another
      person; or (4) We have a legitimate interest in using your information —
      for example, to provide and update our Services; to improve our Services
      so that we can offer you an even better user experience; to safeguard our
      Services; to communicate with you; to measure, gauge, and improve the
      effectiveness of our advertising; and to understand our user retention and
      attrition; to monitor and prevent any problems with our Services; and to
      personalize your experience; or (5) You have given us your consent.
    </p>
    <h3>Sharing Information</h3>
    <h4>How We Share Information</h4>
    <p>
      We share information about you in limited circumstances, and with
      appropriate safeguards on your privacy. These are spelled out below.
    </p>
    <ul>
      <li>
        Subsidiaries and independent contractors: We may disclose information
        about you to our subsidiaries and independent contractors who need the
        information to help us provide our Services or process the information
        on our behalf. We require our subsidiaries and independent contractors
        to follow this Privacy Policy for any personal information that we share
        with them.
      </li>
      <li>
        Third-party vendors: We may share information about you with third-party
        vendors who need the information in order to provide their services to
        us, or to provide their services to you or your site. This includes
        vendors that help us provide our Services to you (like Stripe for
        payment processing); those that assist us with our marketing efforts
        (e.g., by providing tools for identifying a specific marketing target
        group or improving our marketing campaigns, and by placing ads to market
        our services); those that help us understand and enhance our Services
        (like analytics providers); those that make tools to help us run our
        operations (like programs that help us with task management, scheduling,
        word processing, email and other communications, and collaboration among
        our teams); other third-party tools that help us manage operations; and
        companies that make products available on our websites, who may need
        information about you in order to, for example, provide technical or
        other support services to you.
      </li>
      <li>
        Legal and regulatory requirements: We may disclose information about you
        in response to a subpoena, court order, or other governmental request.
      </li>
      <li>
        To protect rights, property, and others: We may disclose information
        about you when we believe in good faith that disclosure is reasonably
        necessary to protect the property or rights of Postdrop, third parties,
        or the public at large.
      </li>
      <li>
        Business transfers: In connection with any merger, sale of company
        assets, or acquisition of all or a portion of our business by another
        company, or in the unlikely event that Postdrop goes out of business or
        enters bankruptcy, user information would likely be one of the assets
        that is transferred or acquired by a third party. If any of these events
        were to happen, this Privacy Policy would continue to apply to your
        information and the party receiving your information may continue to use
        your information, but only consistent with this Privacy Policy.
      </li>
      <li>
        With your consent: We may share and disclose information with your
        consent or at your direction.
      </li>
      <li>
        Aggregated or de-identified information: We may share information that
        has been aggregated or de-identified, so that it can no longer
        reasonably be used to identify you. For instance, we may publish
        aggregate statistics about the use of our Services, or share a hashed
        version of your email address to facilitate customized ad campaigns on
        other platforms.
      </li>
      <li>
        Published support requests: If you send us a request for assistance (for
        example, via a support email or one of our other feedback mechanisms),
        we reserve the right to publish that request in order to clarify or
        respond to your request, or to help us support other users.
      </li>
    </ul>
    <p>
      We have a policy that we do not sell our users' data. We aren't a data
      broker, we don't sell your personal information to data brokers, and we
      don't sell your information to other companies that want to spam you with
      marketing emails. Under a new California law, the California Consumer
      Privacy Act ("CCPA"), some personalized advertising you see online and on
      our services might be considered a "sale" even though we don't share
      information that identifies you personally, like your name or email
      address, as part of our advertising program.
    </p>
    <h4>Information Shared Publicly</h4>
    <p>
      Information that you choose to make public is — you guessed it — disclosed
      publicly. That means information that you include in an email and share in
      a public URL or send to a test user.
    </p>
    <h3>How Long We Keep Information</h3>
    <p>
      We generally discard information about you when it’s no longer needed for
      the purposes for which we collect and use it — described in the section
      above on How and Why We Use Information — and we’re not legally required
      to keep it. For example, we keep the web server logs that record
      information about a visitor to one of Postdrop’s websites, like the
      visitor’s IP address, browser type, and operating system, for
      approximately 30 days. We retain the logs for this period of time in order
      to, among other things, analyze traffic to Postdrop’s websites and
      investigate issues if something goes wrong on one of our websites. As
      another example, when you delete a template, it stays in your Trash folder
      for thirty days in case you change your mind and would like to restore
      that content, because starting from scratch is no fun. After the thirty
      days are up, the deleted content may remain on our backups and caches
      until purged.
    </p>
    <h3>Security</h3>
    <p>
      While no online service is 100% secure, we work very hard to protect
      information about you against unauthorized access, use, alteration, or
      destruction, and take reasonable measures to do so. We monitor our
      Services for potential vulnerabilities and attacks.
    </p>
    <h3>Your Rights</h3>
    <p>
      If you are located in certain parts of the world, including California and
      countries that fall under the scope of the European General Data
      Protection Regulation (aka the “GDPR”), you may have certain rights
      regarding your personal information, like the right to request access to
      or deletion of your data.
    </p>
    <h4>European General Data Protection Regulation (GDPR)</h4>
    <p>
      If you are located in a country that falls under the scope of the GDPR,
      data protection laws give you certain rights with respect to your personal
      data, subject to any exemptions provided by the law, including the rights
      to:
    </p>
    <ul>
      <li>Request access to your personal data;</li>
      <li>Request correction or deletion of your personal data;</li>
      <li>Object to our use and processing of your personal data;</li>
      <li>
        Request that we limit our use and processing of your personal data; and
      </li>
      <li>Request portability of your personal data.</li>
    </ul>
    <p>
      You also have the right to make a complaint to a government supervisory
      authority.
    </p>
    <h4>California Consumer Privacy Act (CCPA)</h4>
    <p>
      The California Consumer Privacy Act (“CCPA”) requires us to provide
      California residents with some additional information about the categories
      of personal information we collect and share, where we get that personal
      information, and how and why we use it. The CCPA also requires us to
      provide a list of the “categories” of personal information we collect, as
      that term is defined in the law, so, here it is. In the last 12 months, we
      collected the following categories of personal information from California
      residents, depending on the Services used:
    </p>
    <ul>
      <li>
        Identifiers (like your name, contact information, and device and online
        identifiers);
      </li>
      <li>
        Commercial information (your billing information and purchase history,
        for example);
      </li>
      <li>
        Characteristics protected by law (for example, you might provide your
        gender as part of a research survey for us);
      </li>
      <li>
        Internet or other electronic network activity information (such as your
        usage of our Services);
      </li>
      <li>
        Geolocation data (such as your location based on your IP address);
      </li>
      <li>
        Audio, electronic, visual or similar information (such as your profile
        picture, if you uploaded one);
      </li>
      <li>Professional or employment-related information; and</li>
      <li>
        Inferences we make (such as likelihood of retention or attrition).
      </li>
    </ul>
    <p>
      If you are a California resident, you have additional rights under the
      CCPA, subject to any exemptions provided by the law, including the right
      to:
    </p>
    <ul>
      <li>
        Request to know the categories of personal information we collect, the
        categories of business or commercial purpose for collecting and using
        it, the categories of sources from which the information came, the
        categories of third parties we share it with, and the specific pieces of
        information we collect about you;
      </li>
      <li>Request deletion of personal information we collect or maintain;</li>
      <li>Opt out of any sale of personal information; and</li>
      <li>
        Not receive discriminatory treatment for exercising your rights under
        the CCPA.
      </li>
    </ul>
    <h4>Contacting Us About These Rights</h4>
    <p>
      You can usually access, correct, or delete your personal data using your
      account settings and tools that we offer, but if you aren’t able to or
      you’d like to contact us about one of the other rights, scroll down to
      “How to Reach Us” to, well, find out how to reach us. When you contact us
      about one of your rights under this section, we’ll need to verify that you
      are the right person before we disclose or delete anything. For example,
      if you are a user, we will need you to contact us from the email address
      associated with your account. You can also designate an authorized agent
      to make a request on your behalf by giving us written authorization. We
      may still require you to verify your identity with us.
    </p>
    <h3>How to Reach Us</h3>
    <p>
      If you have a question about this Privacy Policy, or you would like to
      contact us about any of the rights mentioned in the Your Rights section
      above, please contact us via email hello@postdrop.io. This is the fastest
      way to get a response to your inquiry.
    </p>
    <h3>Other Things You Should Know (Keep Reading!)</h3>
    <h4>Transferring Information</h4>
    <p>
      Because Postdrop’s Services are offered worldwide, the information about
      you that we process when you use the Services in the EU may be used,
      stored, and/or accessed by individuals operating outside the European
      Economic Area (EEA) who work for us, other members of our group of
      companies, or third-party data processors. When providing information
      about you to entities outside the EEA, we will take appropriate measures
      to ensure that the recipient protects your personal information adequately
      in accordance with this Privacy Policy as required by applicable law.
      These measures include:
    </p>
    <ul>
      <li>
        In the case of US based entities, entering into European Commission
        approved standard contractual arrangements with them, or ensuring they
        have signed up to the EU-US Privacy Shield; or
      </li>
      <li>
        In the case of entities based in other countries outside the EEA,
        entering into European Commission approved standard contractual
        arrangements with them.
      </li>
    </ul>
    <p>
      You can ask us for more information about the steps we take to protect
      your personal information when transferring it from the EU.
    </p>
    <h3>Privacy Policy Changes</h3>
    <p>
      Although most changes are likely to be minor, Postdrop may change its
      Privacy Policy from time to time. Postdrop encourages visitors to
      frequently check this page for any changes to its Privacy Policy. If we
      make changes, we will notify you by revising the change log below, and, in
      some cases, we may provide additional notice (like adding a statement to
      our homepage, or sending you a notification through email or your
      dashboard). Your further use of the Services after a change to our Privacy
      Policy will be subject to the updated policy
    </p>
  </>
);

export default function SignUp(_props: Props) {
  return (
    <>
      <Meta title="Privacy Policy" />
      <Public.Header title="Privacy Policy" />
      <Public.Body width="max-w-4xl">
        <div className="prose prose-sm sm:prose md:prose-md lg:prose-lg">
          <Content />
        </div>
      </Public.Body>
    </>
  );
}

SignUp.Layout = Public;
